import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useAuth } from "../../../auth";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";

import DetailCard from "../../../components/DetailCard";
import { FormInput } from "../../../components/FormInput";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog";
import useNotifications from "../../../components/NotificationProvider";
import ColumnHeader from "../../../components/ColumnHeader";
import { FormCheckbox } from "../../../components/FormCheckbox";
import ExposureWarningDialog from "../../../components/ExposureWarningDialog";
import {
  IPreflight,
  MAX_QUANTITY_LENGTH,
  buildDefaultExposureName,
  slugifyExposureName,
  validateExposureName,
} from "../../../types/api/istio.type";
import { FormTextEditor } from "../../../components/FormTextEditor";
import { FormSelect } from "../../../components/FormSelect";
import useLoader from "../../../components/LoaderProvider";
import TenantService from "../../../services/tenant.service";
import EnvironmentService from "../../../services/environment.service";
import IstioService, { exposureErrorDetail } from "../../../services/istio.service";
import { useStoreState, useStoreActions } from "../../../store/hooks";
import { constructTenant } from "../../../utils/utils";
import { IAccountsColumn } from "../../../types/app/accounts.type";
import {
  defaultTenantPreferences,
  environmentDBTiers,
  nodepoolOptions,
  resourceAllocationOptions,
  storageClassOptions,
} from "../../../global/constants";
import { ITenant } from "../../../types/api/tenant.type";
import {
  IEnvironment,
  IPhysicalEnvironment,
} from "../../../types/api/environment.type";

import "./account.scss";

interface ITenantsColumn extends IAccountsColumn {}

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`environment-panel-${index}`}
      aria-labelledby={`environment-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `environment-${index}`,
    "aria-controls": `environment-panel-${index}`,
  };
};

const TenantsColumn = ({ setSelectedColumn }: ITenantsColumn) => {
  const { selectedAccount } = useStoreState((store) => store.account);
  const { selectedOrganization } = useStoreState((store) => store.organization);
  const { selectedLocation } = useStoreState((store) => store.location);
  const { tenants, selectedTenant, totalTenant, loading } = useStoreState(
    (store) => store.tenant
  );
  const { physicalEnvironment } = useStoreState((store) => store.environment);
  const {
    setSelectedTenant,
    clearTenantState,
    getTenantsByLocationId,
    createTenant,
    editTenant,
    deleteTenant,
  } = useStoreActions((store) => store.tenant);
  const {
    getPhysicalEnvDetailsByEnvId,
    createPhysicalEnvironment,
    deletePhysicalEnvironment,
    setPhysicalEnvironment,
  } = useStoreActions((store) => store.environment);
  const { user } = useAuth();
  const { t } = useTranslation("common");
  const {
    control,
    register,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({});
  const {
    control: envControl,
    register: envRegister,
    reset: envReset,
    handleSubmit: envHandleSubmit,
    formState: { errors: envErrors, dirtyFields: envDirtyFields },
  } = useForm({});
  const [page, setPage] = useState(1);
  const [selectedTenantDetail, setSelectedTenantDetail] = useState<any>({});
  const [selectedEnvironment, setSelectedEnvironment] = useState<IEnvironment>(
    {} as IEnvironment
  );
  const [openTenantDialog, setOpenTenantDialog] = useState(false);
  const [openEnvironmentDialog, setOpenEnvironmentDialog] = useState(false);
  const [openEnvFormDialog, setOpenEnvFormDialog] = useState(false);
  const [exposurePreflight, setExposurePreflight] = useState<IPreflight | null>(null);
  const [exposureWarning, setExposureWarning] = useState<IPreflight | null>(null);
  const [exposureInfo, setExposureInfo] = useState<IPreflight | null>(null);
  const exposure = exposureInfo?.existing ?? null;
  const [exposureBusy, setExposureBusy] = useState(false);
  const [exposureGateway, setExposureGateway] = useState<string>("");
  const [checkingCapacity, setCheckingCapacity] = useState(false);
  const [openUnexposeConfirmation, setOpenUnexposeConfirmation] = useState(false);
  // Resolves the "proceed without a virtual service?" dialog so onEnvSubmit can await it.
  const exposureDecision = useRef<((proceed: boolean) => void) | null>(null);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openEnvDeleteConfirmation, setOpenEnvDeleteConfirmation] =
    useState(false);
  const [showSampleTenantPreferences, setShowSampleTenantPreferences] =
    useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [backupStorageAccountOptions, setBackupStorageAccountOptions] = useState<
    { id: string; label: string }[]
  >([]);
  const [backupSchedulePresets, setBackupSchedulePresets] = useState<{
    full: { id: string; label: string; cron: string }[];
    incremental: { id: string; label: string; cron: string }[];
  }>({ full: [], incremental: [] });
  const { setNotification } = useNotifications();
  const [searchTerm, setSearchTerm] = useState("");
  const searchRef = useRef<any>(null);
  const { setLoading } = useLoader();

  useEffect(() => {
    return () => {
      clearTenantState();
      resetSearchInput();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation.loc_id]);

  useEffect(() => {
    loadData(1);
    setSelectedColumn({ id: 3 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation.loc_id, searchTerm]);

  // useEffect(() => {
  //   if (tabValue === 1 || (openEnvFormDialog && selectedEnvironment.env_id)) {
  //     getPhysicalEnvDetails();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [tabValue]);

  useEffect(() => {
    const pe = physicalEnvironment as IPhysicalEnvironment;
    const base: Record<string, unknown> = {
      ...selectedEnvironment,
      ...physicalEnvironment,
      description: selectedEnvironment.env_desc,
    };
    if (!selectedEnvironment.env_id) {
      base.expose_externally = true;
    }
    if (selectedEnvironment.env_id) {
      base.full_backup_schedule =
        pe.full_backup_schedule ??
        (pe.full_backup_schedule_cron ? "__keep__" : "weekly");
      base.incremental_backup_schedule =
        pe.incremental_backup_schedule ??
        (pe.incremental_backup_schedule_cron ? "__keep__" : "daily");
    }
    envReset(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvironment, physicalEnvironment, openEnvFormDialog]);

  useEffect(() => {
    if (!openEnvFormDialog) return;
    EnvironmentService.getBackupStorageAccountOptions()
      .then((res: any) => setBackupStorageAccountOptions(res?.data ?? []))
      .catch(() => setBackupStorageAccountOptions([]));
    EnvironmentService.getBackupSchedulePresets()
      .then((res: any) =>
        setBackupSchedulePresets({
          full: res?.data?.full ?? [],
          incremental: res?.data?.incremental ?? [],
        })
      )
      .catch(() => setBackupSchedulePresets({ full: [], incremental: [] }));
    // Load DB-manager env payload (incl. backup CronJob schedules) whenever the form opens for edit
    if (selectedEnvironment.env_id) {
      getPhysicalEnvDetailsByEnvId({ envId: selectedEnvironment.env_id as number }).catch(() => {
        /* thunk may clear physicalEnvironment; form still usable */
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- easy-peasy thunk stable; avoid dependency churn
  }, [openEnvFormDialog, selectedEnvironment.env_id]);

  // Live capacity hint for the create form. Skipped when editing: exposure is managed from
  // the details pane, not here.
  useEffect(() => {
    if (!openEnvFormDialog || selectedEnvironment.env_id) {
      setExposurePreflight(null);
      return;
    }
    IstioService.preflight()
      .then((res) => setExposurePreflight(res.data))
      .catch(() => setExposurePreflight(null));
  }, [openEnvFormDialog, selectedEnvironment.env_id]);

  // Exposure state for the details pane. Independent of the physical-details call, which
  // 404s whenever the database is scaled to zero.
  const reloadExposure = useCallback(async (envId?: number) => {
    if (!envId) {
      setExposureInfo(null);
      return;
    }
    try {
      // The cheap, unambiguous question first. preflight would answer it too, but it can come
      // back name_taken when the derived default name collides with an unrelated object.
      const { data } = await IstioService.getExposure(envId);
      setExposureInfo({
        can_expose: false,
        reason: "already_exposed",
        existing: data,
        free_count: 0,
        fresh_count: 0,
        declared_count: 0,
        low_watermark: false,
      });
      return;
    } catch {
      // Not exposed, or the lookup failed for another reason — preflight answers both, and
      // returns 200 with a reason (rbac_denied, ports_exhausted) rather than throwing, so it
      // is what keeps the Expose button correctly disabled and explained.
    }
    try {
      setExposureInfo((await IstioService.preflight(envId)).data);
    } catch {
      setExposureInfo(null);
    }
  }, []);

  useEffect(() => {
    if (!openEnvironmentDialog) {
      setExposureInfo(null);
      return;
    }
    reloadExposure(selectedEnvironment.env_id as number);
  }, [openEnvironmentDialog, selectedEnvironment.env_id, reloadExposure]);

  useEffect(() => {
    setShowSampleTenantPreferences(false);
  }, [openTenantDialog]);

  useEffect(() => {
    if (selectedEnvironment.env_id && openEnvironmentDialog && tabValue === 0){
      getPhysicalEnvDetails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvironment, openEnvironmentDialog, tabValue]);

  const getPhysicalEnvDetails = async () => {
    try {
      setLoading(true);
      await getPhysicalEnvDetailsByEnvId({
        envId: selectedEnvironment.env_id as number,
      });
    } catch (error: any) {
      if (tabValue === 1) {
        setNotification({
          message: error?.message,
          open: true,
          type: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (initialPage = 0) => {
    try {
      await getTenantsByLocationId({
        locationId: selectedLocation.loc_uuid as string,
        data: {
          count_per_page: 10,
          page_no: initialPage || page,
          filter: {
            tenant_name: searchTerm,
          },
          show_deleted: false,
        },
      });
      setPage((initialPage || page) + 1);
    } catch (error) {}
  };

  const onSubmit = async (values: any) => {
    try {
      let tenant_uuid = values.tenant_uuid;
      values.loc_id = selectedLocation.loc_id;
      const constructedData = constructTenant(values);
      if (tenant_uuid) {
        await editTenant({ data: constructedData, tenant_uuid });
      } else {
        await createTenant(constructedData);
      }
      setOpenTenantDialog(false);
      setNotification({
        message: `Tenant ${tenant_uuid ? "updated" : "created"} successfully`,
        open: true,
      });
      loadData(1);
      reset({});
    } catch (e) {
      setNotification({
        message: "Error occurred",
        open: true,
        type: "error",
      });
      console.error("----", e);
    }
  };

  /** Blocks until the operator answers the exposure dialog. */
  const askExposureDecision = (preflight: IPreflight) =>
    new Promise<boolean>((resolve) => {
      exposureDecision.current = resolve;
      setExposureWarning(preflight);
    });

  const resolveExposureDecision = (proceed: boolean) => {
    setExposureWarning(null);
    const resolve = exposureDecision.current;
    exposureDecision.current = null;
    resolve?.(proceed);
  };

  const onEnvSubmit = async (values: any) => {
    try {
      const selectedEnvId = selectedEnvironment.env_uuid;
      const userData = user as any;
      const tenantId = selectedTenant.tenant_id as number;

      const logicalEnvData: Record<string, unknown> = {
        description: values.description,
        is_batch: Boolean(values.is_batch),
        is_subscription: Boolean(values.is_subscription),
        is_trial: Boolean(values.is_trial),
        make_prod: Boolean(values.make_prod),
        rule_engine: Boolean(values.rule_engine),
        owner: userData["https://controlrooms.ai/user"].user_id as number,
        status: values.status || "starting",
        tenant_id: selectedTenant.tenant_id as number,
        environment_uuid: selectedEnvId,
      };
      if (selectedEnvId) {
        if (
          envDirtyFields.full_backup_schedule &&
          values.full_backup_schedule &&
          values.full_backup_schedule !== "__keep__"
        ) {
          logicalEnvData.full_backup_schedule = values.full_backup_schedule;
        }
        if (
          envDirtyFields.incremental_backup_schedule &&
          values.incremental_backup_schedule &&
          values.incremental_backup_schedule !== "__keep__"
        ) {
          logicalEnvData.incremental_backup_schedule = values.incremental_backup_schedule;
        }
      }
      const exposeExternally = !selectedEnvId && Boolean(values.expose_externally);
      // buildDefaultExposureName mirrors the server: each part slugified before joining, and a
      // part already present dropped — tenant and environment usually carry the same label.
      const exposureName = exposeExternally
        ? values.exposure_name
          ? slugifyExposureName(values.exposure_name)
          : buildDefaultExposureName(selectedTenant.tenant_name, values.description)
        : "";
      let proceedWithoutExposure = false;

      // Before the first write anywhere: a refusal after the accounts environment exists
      // would leave a stray environment with no database behind it.
      if (exposeExternally) {
        setCheckingCapacity(true);
        // preflight is the authoritative check: it derives from `unreserved` server-side and
        // also covers name_taken / invalid_name. env_id is optional so the create path can use
        // it before the environment exists.
        let check: IPreflight | null = null;
        try {
          check = (await IstioService.preflight(undefined, exposureName)).data;
          setExposureGateway(check.gateway || "");
        } catch {
          // #5: "let the API decide" stranded a logical environment — ensure_capacity_before_create
          // 503s *after* createEnvironment has already run. Ask instead of guessing.
          check = {
            can_expose: false,
            reason: "capacity_unknown",
            free_count: 0,
            fresh_count: 0,
            declared_count: 0,
            low_watermark: false,
          };
        } finally {
          setCheckingCapacity(false);
        }
        if (check && !check.can_expose) {
          // Nothing has been created yet, so the operator can still back out. The loader
          // only goes up once they have answered.
          const proceed = await askExposureDecision(check);
          if (!proceed) return;
          proceedWithoutExposure = true;
        }
      }

      setLoading(true);

      let envResponse = {} as { data: IEnvironment };
      if (selectedEnvId) {
        envResponse = await EnvironmentService.updateEnvInfo(
          logicalEnvData,
          selectedEnvironment.env_id as number
        );
        setSelectedEnvironment(envResponse?.data);
        setEnvironmentList(selectedTenant.tenant_uuid as string);
      } else {
        envResponse = await EnvironmentService.createEnvironment(
          logicalEnvData
        );
      }
      const envID = envResponse?.data?.env_id;
      const envUUID = envResponse?.data?.env_uuid;
      const rule_engine = logicalEnvData.rule_engine;
      const physicalEnvData = {
        cpu_req: values.cpu_req,
        id: envID,
        uuid: envUUID,
        rule_engine: rule_engine,
        memory_req: values.memory_req,
        password: values.password,
        storage: values.storage,
        username: values.username,
        storage_class: values.storage_class || "cr-basic-retain",
        nodepool: values.nodepool || "database",
        resource_allocation: values.resource_allocation || "request_only",
        cpu_bkp: values.cpu_bkp || "250m",
        memory_bkp: values.memory_bkp || "256Mi",
        backup_storage_account: values.backup_storage_account ?? "",
        expose_externally: exposeExternally,
        proceed_without_exposure: proceedWithoutExposure,
        exposure_name: exposureName || undefined,
        full_backup_schedule: values.full_backup_schedule ?? "weekly",
        incremental_backup_schedule: values.incremental_backup_schedule ?? "daily",
        // tier: values.tier, // TODO: Need to enable this in future
      };
      let exposureWarningMessage = "";
      if (!selectedEnvId) {
        const created: any = await createPhysicalEnvironment({
          data: physicalEnvData,
          options: {
            headers: { "tenant-id": tenantId },
          },
        });
        exposureWarningMessage = created?.warning?.message || "";
      }
      setOpenEnvFormDialog(false);
      setNotification({
        message: exposureWarningMessage
          ? `Environment created, but it was not exposed: ${exposureWarningMessage}`
          : `Environment ${selectedEnvId ? "updated" : "created"} successfully`,
        open: true,
        type: exposureWarningMessage ? "warning" : undefined,
      });
      loadData(1);
      envReset({});
      setLoading(false);
    } catch (error) {
      setLoading(false);
      const detail = exposureErrorDetail(error);
      setNotification({
        message: detail.message
          ? `Could not create the environment: ${detail.message}`
          : "Error occurred while creating environment",
        open: true,
        type: "error",
      });
    }
  };

  const handleExpose = async () => {
    try {
      setExposureBusy(true);
      const { data } = await IstioService.createExposure(
        selectedEnvironment.env_id as number,
        undefined,
        selectedTenant.tenant_id as number
      );
      reloadExposure(selectedEnvironment.env_id as number);
      setNotification({
        message: `Exposed on port ${data.port}`,
        open: true,
      });
      getPhysicalEnvDetailsByEnvId({ envId: selectedEnvironment.env_id as number }).catch(() => {});
    } catch (e) {
      const detail = exposureErrorDetail(e);
      setNotification({
        message: detail.message || "Could not expose the environment",
        open: true,
        type: detail.code === "allocation_contended" ? "warning" : "error",
      });
    } finally {
      setExposureBusy(false);
    }
  };

  const handleUnexpose = async () => {
    try {
      setExposureBusy(true);
      await IstioService.deleteExposure(
        selectedEnvironment.env_id as number,
        selectedTenant.tenant_id as number,
        user?.email || user?.name || undefined
      );
      reloadExposure(selectedEnvironment.env_id as number);
      setOpenUnexposeConfirmation(false);
      setNotification({ message: "Exposure removed", open: true });
      getPhysicalEnvDetailsByEnvId({ envId: selectedEnvironment.env_id as number }).catch(() => {});
    } catch (e) {
      setNotification({
        message: exposureErrorDetail(e).message || "Could not remove the exposure",
        open: true,
        type: "error",
      });
    } finally {
      setExposureBusy(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTenant(selectedTenant.tenant_uuid as string);
      setOpenDeleteConfirmation(false);
      setNotification({ message: "Tenant deleted successfully", open: true });
      setSelectedColumn({ id: 3, name: "location" });
      loadData(1);
    } catch (e) {
      setNotification({
        message: "Error occurred",
        open: true,
        type: "error",
      });
      console.error("----", e);
    }
  };

  const handleEnvDelete = async () => {
    try {
      setLoading(true);
      await deletePhysicalEnvironment({
        envId: selectedEnvironment.env_id as number,
        envUuid: selectedEnvironment.env_uuid as string,
      });
      setOpenEnvDeleteConfirmation(false);
      setOpenEnvironmentDialog(false);
      setNotification({ message: "Tenant deleted successfully", open: true });
      setEnvironmentList(selectedTenant.tenant_uuid as string);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setNotification({
        message: "Error occurred",
        open: true,
        type: "error",
      });
      console.error("----", e);
    }
  };

  const setEnvironmentList = async (tenantId: string) => {
    const result = await TenantService.getTenantEnvironmentById(tenantId);
    const tenantEnvs = result.data;
    const envElements = tenantEnvs.map((env: IEnvironment) => (
      <ListItem
        key={env.env_id}
        sx={{ py: 0, minHeight: 32, cursor: "pointer" }}
        onClick={() => {
          setOpenEnvironmentDialog(true);
          setSelectedEnvironment(env);
          setTabValue(0);
        }}
        disableGutters
      >
        <Badge color="success" variant="dot" sx={{ mx: "16px !important" }} />
        <ListItemText secondary={`#${env.env_id} - ${env.env_desc}`} />
      </ListItem>
    ));
    setSelectedTenantDetail(envElements);
  };

  const handleOnCardClick = async (data: ITenant) => {
    setSelectedColumn({ id: 4, name: "tenant" });
    setSelectedTenant(data);
    setEnvironmentList(data.tenant_uuid as string);
  };

  const handleEditClick = () => {
    const preferences = JSON.stringify(
      selectedTenant.preferences,
      undefined,
      2
    );
    reset({ ...selectedTenant, preferences });
    setOpenTenantDialog(true);
  };

  const handleDeleteClick = () => {
    setOpenDeleteConfirmation(true);
  };

  const resetSearchInput = () => {
    if (searchRef.current !== null) {
      searchRef?.current.resetSearch();
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEnvEdit = () => {
    setOpenEnvFormDialog(true);
  };

  return (
    <>
      <Box sx={{ borderRight: 1, borderColor: "#5B5E67", pl: 1 }}>
        <ColumnHeader
          title={t("common:tenants.title")}
          handleOnSearch={setSearchTerm}
          handleOnAdd={() => {
            setOpenTenantDialog(true);
          }}
          ref={searchRef}
        />
        <Box
          id="tenantScroll"
          sx={{
            height: "calc(100vh - 150px)",
            "&::-webkit-scrollbar": {
              width: 5,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#757575",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "primary.main",
              borderRadius: 2,
            },
            overflow: "auto",
            pr: 1,
          }}
        >
          <InfiniteScroll
            dataLength={tenants.length}
            next={loadData}
            hasMore={tenants.length < totalTenant}
            loader={<h4>Loading...</h4>}
            scrollableTarget="tenantScroll"
          >
            {loading && (
              <Typography align="center" p={2}>
                Loading...
              </Typography>
            )}
            {tenants.map((tenant: ITenant, index: number) => (
              <DetailCard
                key={tenant.tenant_id}
                data={tenant}
                details={selectedTenantDetail}
                title={tenant.tenant_name}
                subheader={`#${tenant.tenant_id}`}
                id={tenant.tenant_id}
                showDetailView={tenant.tenant_id === selectedTenant.tenant_id}
                handleOnCardClick={handleOnCardClick}
                handleEdit={handleEditClick}
                handleDelete={handleDeleteClick}
                hasAddEnv={true}
                handleAddEnv={() => {
                  setSelectedEnvironment({} as IEnvironment);
                  setPhysicalEnvironment({} as IPhysicalEnvironment);
                  setOpenEnvFormDialog(true);
                }}
              />
            ))}
            {!tenants.length && !loading && (
              <Typography align="center" p={2}>
                No records found
              </Typography>
            )}
          </InfiniteScroll>
        </Box>
      </Box>

      <Dialog
        fullWidth={true}
        maxWidth="md"
        open={openTenantDialog}
        onClose={() => setOpenTenantDialog(false)}
      >
        <DialogTitle>{`${
          getValues("tenant_id") ? "Edit" : "Create"
        } tenant`}</DialogTitle>
        <Divider />
        <DialogContent>
          <Box component="main">
            <Typography>{`${selectedAccount.account_name} > ${selectedOrganization.org_name} > ${selectedLocation.loc_name}`}</Typography>
            <form>
              <Grid
                container
                spacing={2}
                mt={1}
                pr={2}
                pb={2}
                sx={{ overflow: "auto" }}
              >
                <Grid item xs={8}>
                  <FormInput
                    name="tenant_name"
                    type="string"
                    id="tenant_name"
                    label="Name"
                    rules={{
                      required: "This field is required",
                    }}
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    mt={2}
                    color="#888"
                    sx={{ fontWeight: "bold" }}
                  >
                    Preferences
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <FormTextEditor
                    name="preferences"
                    id="preferences"
                    label="preferences"
                    language={"yml"}
                    minHeight={100}
                    defaultValue={JSON.stringify(
                      defaultTenantPreferences,
                      undefined,
                      2
                    )}
                    rules={{
                      validate: (value: string) => {
                        try {
                          return typeof JSON.parse(value) === "object"
                            ? true
                            : "Please enter a valid object";
                        } catch (err) {
                          return "Please enter a valid object";
                        }
                      },
                    }}
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    mt={2}
                    color="#888"
                    sx={{ fontWeight: "bold" }}
                    onClick={() => {
                      setShowSampleTenantPreferences(
                        !showSampleTenantPreferences
                      );
                    }}
                  >
                    <Link href="#">{`${
                      showSampleTenantPreferences ? "Close" : "Show"
                    } sample tenant preferences`}</Link>
                  </Typography>
                </Grid>
                {showSampleTenantPreferences && (
                  <Grid item xs={10}>
                    <FormTextEditor
                      name="samplePreferences"
                      id="samplePreferences"
                      label="samplePreferences"
                      language={"yml"}
                      minHeight={100}
                      rules={{}}
                      defaultValue={JSON.stringify(
                        defaultTenantPreferences,
                        undefined,
                        2
                      )}
                      readOnly
                      control={control}
                      register={register}
                      errors={errors}
                    />
                  </Grid>
                )}
              </Grid>
            </form>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            sx={{ mr: 2 }}
            onClick={() => {
              setOpenTenantDialog(false);
              reset({});
            }}
          >
            Cancel
          </Button>
          <Button
            sx={{ mr: 2 }}
            variant="contained"
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={openEnvironmentDialog}
        onClose={() => setOpenEnvironmentDialog(false)}
      >
        <DialogTitle>
          {selectedEnvironment.env_desc}
          <Box component="main">
            <Typography>{`${selectedAccount.account_name} > ${selectedOrganization.org_name} > ${selectedLocation.loc_name} > ${selectedTenant.tenant_name}`}</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box component="main">
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Logical" {...a11yProps(0)} />
                <Tab label="Physical" {...a11yProps(1)} />
              </Tabs>
            </Box>
            <TabPanel value={tabValue} index={0}>
              <ListItem disableGutters>
                <Badge
                  color="success"
                  variant="dot"
                  sx={{ mx: "16px !important" }}
                />
                <ListItemText
                  primary={`Environment id: ${selectedEnvironment.env_id}`}
                />
              </ListItem>
              <ListItem disableGutters>
                <Badge
                  color="success"
                  variant="dot"
                  sx={{ mx: "16px !important" }}
                />
                <ListItemText
                  primary={`Environment description: ${selectedEnvironment.env_desc}`}
                />
              </ListItem>
              <ListItem disableGutters>
                <Badge
                  color="success"
                  variant="dot"
                  sx={{ mx: "16px !important" }}
                />
                <ListItemText
                  primary={`Environment type: ${
                    selectedEnvironment.is_batch ? "Batch" : "Stream"
                  }`}
                />
              </ListItem>
              <ListItem disableGutters>
                <Badge
                  color="success"
                  variant="dot"
                  sx={{ mx: "16px !important" }}
                />
                <ListItemText
                  primary={`Subscription: ${selectedEnvironment.is_subscription}`}
                />
              </ListItem>
              <ListItem disableGutters>
                <Badge
                  color="success"
                  variant="dot"
                  sx={{ mx: "16px !important" }}
                />
                <ListItemText
                  primary={`Trial: ${selectedEnvironment.is_trial}`}
                />
              </ListItem>
              <ListItem disableGutters>
                <Badge
                  color="success"
                  variant="dot"
                  sx={{ mx: "16px !important" }}
                />
                <ListItemText
                  primary={`Resource group: ${selectedEnvironment.resource_group}`}
                />
              </ListItem>
              <ListItem disableGutters>
                <Badge
                  color="success"
                  variant="dot"
                  sx={{ mx: "16px !important" }}
                />
                <ListItemText
                  primary={`Status: ${selectedEnvironment.status}`}
                />
              </ListItem>
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <Grid container sx={{ overflow: "auto" }}>
                {physicalEnvironment?.id ? (
                  <>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`Environment id: ${
                            physicalEnvironment?.id || ""
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`Username: ${
                            physicalEnvironment?.username || ""
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`CPUs: ${physicalEnvironment?.cpus || ""}`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`CPU backup: ${
                            physicalEnvironment?.cpu_bkp || ""
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`CPU request: ${
                            physicalEnvironment?.cpu_req || ""
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`Memory: ${
                            physicalEnvironment?.memory || ""
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`Memory backup: ${
                            physicalEnvironment?.memory_bkp || ""
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`Memory request: ${
                            physicalEnvironment?.memory_req || ""
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`Storage: ${
                            physicalEnvironment?.storage || ""
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`Storage available: ${
                            physicalEnvironment?.storage_available || ""
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`Status: ${
                            physicalEnvironment?.status || ""
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`IP Address: ${
                            physicalEnvironment?.ip_address || "N/A"
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`Node: ${
                            physicalEnvironment?.node || "N/A"
                          }`}
                        />
                      </ListItem>
                    </Grid>
                    <Grid item xs={6}>
                      <ListItem disableGutters>
                        <Badge
                          color="success"
                          variant="dot"
                          sx={{ mx: "16px !important" }}
                        />
                        <ListItemText
                          primary={`Age: ${physicalEnvironment?.age || "N/A"}`}
                        />
                      </ListItem>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} m={2}>
                    No data found — the database reports no physical details (it is scaled to
                    zero, or its pod is not running).
                  </Grid>
                )}
                {/* Outside the block above on purpose: exposure is managed through Istio and
                    stays meaningful even when the database itself reports nothing. */}
                <Grid item xs={12}>
                  <ListItem
                    disableGutters
                    sx={{
                      // Long exposure names and the shadowed explanation used to push the
                      // buttons past the edge and force a horizontal scrollbar.
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      rowGap: 1,
                      columnGap: 1,
                      pr: 1,
                    }}
                  >
                    <Badge
                      color={
                        !exposure ? "warning" : exposure.serving === false ? "error" : "success"
                      }
                      variant="dot"
                      sx={{ mx: "16px !important", mt: "10px", flexShrink: 0 }}
                    />
                    <ListItemText
                      sx={{ flex: "1 1 260px", minWidth: 0, my: 0, wordBreak: "break-word" }}
                      primary={
                        exposure
                          ? `External access: port ${exposure.port} (${exposure.name})${
                              exposure.serving === false
                                ? " — carries no traffic"
                                : exposure.managed
                                ? ""
                                : " — created by hand"
                            }`
                          : exposureInfo?.reason === "ports_exhausted"
                          ? "External access: not exposed — no Istio ports available"
                          : exposureInfo?.reason === "rbac_denied"
                          ? "External access: unavailable"
                          : "External access: not exposed"
                      }
                      secondary={
                        exposure?.serving === false
                          ? "Another VirtualService claimed this port first, so Istio routes it " +
                            "elsewhere. Move this one to a free port or remove it."
                          : exposure?.connection_hint || undefined
                      }
                      secondaryTypographyProps={
                        exposure?.serving === false ? { color: "error" } : undefined
                      }
                    />
                    {/* Never offer the host:port of an object that is not serving: that address
                        reaches whichever VirtualService won the port, i.e. another environment.
                        The API withholds the hint too; this does not rely on it. */}
                    {exposure?.connection_hint && exposure.serving !== false && (
                      <Tooltip title="Copy host:port">
                        <IconButton
                          sx={{ flexShrink: 0 }}
                          size="small"
                          onClick={() =>
                            navigator.clipboard?.writeText(exposure.connection_hint as string)
                          }
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!exposure && (
                      <Tooltip title={exposureInfo?.can_expose ? "" : exposureInfo?.message || ""}>
                        <span style={{ flexShrink: 0 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ whiteSpace: "nowrap" }}
                            disabled={exposureBusy || exposureInfo?.can_expose === false}
                            onClick={handleExpose}
                          >
                            Expose
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {exposure?.managed && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                        disabled={exposureBusy}
                        onClick={() => setOpenUnexposeConfirmation(true)}
                      >
                        Unexpose
                      </Button>
                    )}
                  </ListItem>
                </Grid>
              </Grid>
            </TabPanel>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <div>
            <Button
              variant="outlined"
              sx={{ mx: 2 }}
              onClick={() => setOpenEnvDeleteConfirmation(true)}
            >
              Delete
            </Button>
            <Button
              variant="outlined"
              sx={{ mx: 2 }}
              onClick={() => handleEnvEdit()}
            >
              Edit
            </Button>
          </div>
          <Button
            sx={{ mr: 2 }}
            onClick={() => setOpenEnvironmentDialog(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        open={openEnvFormDialog}
        onClose={() => setOpenEnvFormDialog(false)}
      >
        <DialogTitle>{`Create environment`}</DialogTitle>
        <Divider />
        <DialogContent>
          <Box component="main">
            <Typography>{`${selectedAccount.account_name} > ${selectedOrganization.org_name} > ${selectedLocation.loc_name} > ${selectedTenant.tenant_name}`}</Typography>
            <form>
              <Grid
                container
                spacing={2}
                mt={1}
                pr={2}
                pb={2}
                sx={{ overflow: "auto" }}
              >
                <Grid item xs={12}>
                  Logical Entities
                </Grid>
                <Grid item xs={4}>
                  <FormInput
                    name="description"
                    type="string"
                    id="description"
                    label="Description"
                    required
                    rules={{}}
                    control={envControl}
                    register={envRegister}
                    errors={envErrors}
                  />
                </Grid>
                <Grid item xs={8}></Grid>
                <Grid item xs={2}>
                  <FormCheckbox
                    name="is_batch"
                    id="is_batch"
                    label="Is batch"
                    required
                    rules={{}}
                    control={envControl}
                    register={envRegister}
                    errors={envErrors}
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormCheckbox
                    name="is_subscription"
                    id="is_subscription"
                    label="Is subscription"
                    required
                    rules={{}}
                    control={envControl}
                    register={envRegister}
                    errors={envErrors}
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormCheckbox
                    name="is_trial"
                    id="is_trial"
                    label="Is trial"
                    required
                    rules={{}}
                    control={envControl}
                    register={envRegister}
                    errors={envErrors}
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormCheckbox
                    name="make_prod"
                    id="make_prod"
                    label="Make prod"
                    rules={{}}
                    control={envControl}
                    register={envRegister}
                    errors={envErrors}
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormCheckbox
                    name="rule_engine"
                    id="rule_engine"
                    label="Rule Engine"
                    rules={{}}
                    control={envControl}
                    register={envRegister}
                    errors={envErrors}
                  />
                </Grid>
                {!selectedEnvironment?.env_id && (
                  <>
                    <Grid item xs={12}>
                      External access
                    </Grid>
                    <Grid item xs={3}>
                      <Tooltip
                        title="Creates an Istio VirtualService so the database is reachable from outside the cluster on a dedicated TCP port."
                        placement="top"
                      >
                        <Box sx={{ width: "100%" }}>
                          <FormCheckbox
                            name="expose_externally"
                            id="expose_externally"
                            label="Expose externally"
                            defaultValue={true}
                            rules={{}}
                            control={envControl}
                            register={envRegister}
                            errors={envErrors}
                          />
                        </Box>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={5}>
                      <FormInput
                        name="exposure_name"
                        type="string"
                        id="exposure_name"
                        label="Exposure name"
                        helperText={
                          exposurePreflight?.reason === "rbac_denied"
                            ? "External access is unavailable"
                            : exposurePreflight?.next_port
                            ? `tsdb-internal-<name> on port ${exposurePreflight.next_port}`
                            : "Derived from tenant and description when left empty"
                        }
                        rules={{
                          validate: (value: string) =>
                            !value || validateExposureName(slugifyExposureName(value)) === null ||
                            (validateExposureName(slugifyExposureName(value)) as string),
                        }}
                        control={envControl}
                        register={envRegister}
                        errors={envErrors}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      {exposurePreflight && exposurePreflight.free_count === 0 && (
                        <Alert severity="error" sx={{ py: 0 }}>
                          No ports left — you will be asked to confirm on save.
                        </Alert>
                      )}
                      {exposurePreflight &&
                        exposurePreflight.low_watermark &&
                        exposurePreflight.free_count > 0 && (
                          <Alert severity="warning" sx={{ py: 0 }}>
                            Only {exposurePreflight.free_count} Istio port
                            {exposurePreflight.free_count === 1 ? "" : "s"} left.
                          </Alert>
                        )}
                    </Grid>
                  </>
                )}
                {!selectedEnvironment?.env_id && (
                  <>
                    <Grid item xs={12}>
                      Backup container (pgbackrest)
                    </Grid>
                    <Grid item xs={4}>
                      <Tooltip
                        title="Backup container (pgbackrest): these values are set as both request and limit. pgbackrest is more CPU hungry than memory."
                        placement="top"
                      >
                        <Box sx={{ width: "100%" }}>
                          <FormInput
                            name="cpu_bkp"
                            type="string"
                            id="cpu_bkp"
                            label="Backup container CPU"
                            helperText="ex: 250m, 500m"
                            defaultValue="250m"
                            rules={{}}
                            control={envControl}
                            register={envRegister}
                            errors={envErrors}
                          />
                        </Box>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={4}>
                      <Tooltip
                        title="Backup container (pgbackrest): these values are set as both request and limit. pgbackrest is more CPU hungry than memory."
                        placement="top"
                      >
                        <Box sx={{ width: "100%" }}>
                          <FormInput
                            name="memory_bkp"
                            type="string"
                            id="memory_bkp"
                            label="Backup container Memory"
                            helperText="ex: 256Mi, 512Mi"
                            defaultValue="256Mi"
                            rules={{}}
                            control={envControl}
                            register={envRegister}
                            errors={envErrors}
                          />
                        </Box>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                      Physical Entities
                    </Grid>
                    <Grid item xs={4}>
                      <FormInput
                        name="username"
                        type="string"
                        id="username"
                        label="Username"
                        rules={{}}
                        control={envControl}
                        register={envRegister}
                        errors={envErrors}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormInput
                        name="password"
                        type="password"
                        id="password"
                        label="Password"
                        helperText="Min 8 characters required"
                        rules={{
                          minLength: {
                            value: 8,
                            message: "Min 8 characters required",
                          },
                        }}
                        control={envControl}
                        register={envRegister}
                        errors={envErrors}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormInput
                        name="cpu_req"
                        type="string"
                        id="cpu_req"
                        label="Minimum CPUs"
                        required
                        helperText="ex: 2, 500m"
                        rules={{
                          required: "Minimum CPUs is required",
                          maxLength: { value: MAX_QUANTITY_LENGTH, message: `At most ${MAX_QUANTITY_LENGTH} characters (API limit)` },
                          pattern: {
                            value: /^\d+(\.\d+)?m?$/,
                            message: "Use cores (2) or millicores (500m)",
                          },
                        }}
                        control={envControl}
                        register={envRegister}
                        errors={envErrors}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormInput
                        name="memory_req"
                        type="string"
                        id="memory_req"
                        label="Minimum Memory"
                        required
                        helperText="ex: 600Mi, 1Gi"
                        rules={{
                          required: "Minimum Memory is required",
                          maxLength: { value: MAX_QUANTITY_LENGTH, message: `At most ${MAX_QUANTITY_LENGTH} characters (API limit)` },
                          pattern: {
                            value: /^\d+(\.\d+)?(Mi|Gi|G)$/,
                            message: "Include a unit — e.g. 1Gi, 600Mi. A unitless number means bytes.",
                          },
                        }}
                        control={envControl}
                        register={envRegister}
                        errors={envErrors}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormInput
                        name="storage"
                        type="string"
                        id="storage"
                        label="Storage"
                        required
                        helperText="ex: 10Gi, 20Gi"
                        rules={{
                          required: "Storage is required",
                          maxLength: { value: MAX_QUANTITY_LENGTH, message: `At most ${MAX_QUANTITY_LENGTH} characters (API limit)` },
                          pattern: {
                            value: /^\d+(\.\d+)?(Mi|Gi|G)$/,
                            message: "Include a unit — e.g. 10Gi, 500Mi. A unitless number means bytes.",
                          },
                        }}
                        control={envControl}
                        register={envRegister}
                        errors={envErrors}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormSelect
                        name="storage_class"
                        id="storage_class"
                        label="Disk Type"
                        options={storageClassOptions}
                        defaultValue="cr-basic-retain"
                        rules={{}}
                        control={envControl}
                        register={envRegister}
                        errors={envErrors}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormSelect
                        name="tier"
                        id="tier"
                        label="Tier"
                        options={environmentDBTiers}
                        rules={{}}
                        control={envControl}
                        register={envRegister}
                        errors={envErrors}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormSelect
                        name="nodepool"
                        id="nodepool"
                        label="Node pool"
                        options={nodepoolOptions}
                        defaultValue="database"
                        rules={{}}
                        control={envControl}
                        register={envRegister}
                        errors={envErrors}
                      />
                    </Grid>
                    {!selectedEnvironment.env_uuid && (
                      <Grid item xs={4}>
                        <FormSelect
                          name="backup_storage_account"
                          id="backup_storage_account"
                          label="Backup storage account"
                          options={backupStorageAccountOptions.map((o) => ({
                            label: o.label,
                            value: o.id,
                          }))}
                          defaultValue=""
                          rules={{}}
                          control={envControl}
                          register={envRegister}
                          errors={envErrors}
                        />
                      </Grid>
                    )}
                    <Grid item xs={4}>
                      <Tooltip
                        title="Request: minimum guaranteed resources. Limit: maximum allowed. Choose whether limits follow cluster defaults or match your requested values."
                        placement="top"
                      >
                        <Box sx={{ width: "100%" }}>
                          <FormSelect
                            name="resource_allocation"
                            id="resource_allocation"
                            label="Resource allocation"
                            options={resourceAllocationOptions.map(({ label, value }) => ({ label, value }))}
                            defaultValue="request_only"
                            rules={{}}
                            control={envControl}
                            register={envRegister}
                            errors={envErrors}
                          />
                        </Box>
                      </Tooltip>
                    </Grid>
                  </>
                )}
                {(!selectedEnvironment.env_uuid || !!selectedEnvironment.env_id) && (
                  <>
                    <Grid item xs={4}>
                      <FormSelect
                        name="full_backup_schedule"
                        id="full_backup_schedule"
                        label="Full backup schedule"
                        options={[
                          ...(selectedEnvironment.env_uuid &&
                          (physicalEnvironment as IPhysicalEnvironment).full_backup_schedule ==
                            null &&
                          (physicalEnvironment as IPhysicalEnvironment).full_backup_schedule_cron
                            ? [
                                {
                                  label: "Keep current (legacy / custom cron)",
                                  value: "__keep__",
                                },
                              ]
                            : []),
                          ...backupSchedulePresets.full.map((o) => ({
                            label: o.label,
                            value: o.id,
                          })),
                        ]}
                        defaultValue="weekly"
                        rules={{}}
                        control={envControl}
                        register={envRegister}
                        errors={envErrors}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormSelect
                        name="incremental_backup_schedule"
                        id="incremental_backup_schedule"
                        label="Incremental backup schedule"
                        options={[
                          ...(selectedEnvironment.env_uuid &&
                          (physicalEnvironment as IPhysicalEnvironment)
                            .incremental_backup_schedule == null &&
                          (physicalEnvironment as IPhysicalEnvironment)
                            .incremental_backup_schedule_cron
                            ? [
                                {
                                  label: "Keep current (legacy / custom cron)",
                                  value: "__keep__",
                                },
                              ]
                            : []),
                          ...backupSchedulePresets.incremental.map((o) => ({
                            label: o.label,
                            value: o.id,
                          })),
                        ]}
                        defaultValue="daily"
                        rules={{}}
                        control={envControl}
                        register={envRegister}
                        errors={envErrors}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </form>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button sx={{ mr: 2 }} onClick={() => setOpenEnvFormDialog(false)}>
            Cancel
          </Button>
          <Button
            sx={{ mr: 2 }}
            variant="contained"
            disabled={checkingCapacity}
            onClick={envHandleSubmit(onEnvSubmit)}
          >
            {checkingCapacity ? "Checking capacity…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={openEnvDeleteConfirmation}
        setOpen={setOpenEnvDeleteConfirmation}
        onProceed={handleEnvDelete}
        title="Delete environment"
        content="If you are sure you want to delete the selected environment?"
        confirmationText={selectedEnvironment.env_desc}
      />
      <ExposureWarningDialog
        open={!!exposureWarning}
        preflight={exposureWarning}
        gateway={exposureGateway}
        onProceed={() => resolveExposureDecision(true)}
        onCancel={() => resolveExposureDecision(false)}
      />
      <ConfirmationDialog
        open={openUnexposeConfirmation}
        setOpen={setOpenUnexposeConfirmation}
        onProceed={handleUnexpose}
        title="Remove external access"
        content={`This disconnects every client using port ${
          exposure?.port ?? ""
        }. The database itself is not affected.`}
        confirmationText={exposure?.name}
      />
      <ConfirmationDialog
        open={openDeleteConfirmation}
        setOpen={setOpenDeleteConfirmation}
        onProceed={handleDelete}
        title="Delete"
        content="If you are sure you want to delete the selected tenant?"
        confirmationText={selectedTenant.tenant_name}
      />
    </>
  );
};

export default React.memo(TenantsColumn);
